// @ts-nocheck
import React, { useState, useRef, useCallback } from 'react';
import { toBlob } from 'html-to-image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faCopy,
  faChevronLeft,
  faChevronRight,
  faCheck,
  faXmark,
} from '@fortawesome/pro-thin-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import Button from '@/components/Button';
import { InvestorProfile } from '../../constants';

import bloomieAnalytical from '@/assets/bloomie_analytical.png';
import bloomieBalanced from '@/assets/bloomie_balanced.png';
import bloomieCollaborative from '@/assets/bloomie_collaborative.png';
import bloomieContrarian from '@/assets/bloomie_contrarian.png';
import bloomieStrategic from '@/assets/bloomie_strategic.png';
import bloomieVisionary from '@/assets/bloomie_visionary.png';

const BLOOMIE_IMAGES: Record<string, typeof bloomieAnalytical> = {
  ANALYTICAL: bloomieAnalytical,
  BALANCED: bloomieBalanced,
  COLLABORATIVE: bloomieCollaborative,
  CONTRARIAN: bloomieContrarian,
  STRATEGIC: bloomieStrategic,
  VISIONARY: bloomieVisionary,
};

const PIE_COLORS = [
  '#3b82f6', '#2dd4bf', '#4ade80', '#facc15',
  '#fb923c', '#f87171', '#a78bfa', '#38bdf8', '#818cf8',
];

// ── Helpers ──

const decodeEntities = (s: string) =>
  s.replace(/&apos;/g, "'").replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');

const stripHtml = (s: string) => decodeEntities(s.replace(/<[^>]*>/g, ''));

// ── Pure SVG Radar Chart (no recharts — reliable for image capture) ──

const SVGRadarChart: React.FC<{ data: { subject: string; A: number; fullMark: number }[]; size: number; color?: string; labelSize?: number }> = ({
  data, size, color = '#99f6e4', labelSize = 34,
}) => {
  const cx = size / 2;
  const cy = size / 2;
  const levels = 4;
  const maxR = size * 0.36;
  const n = data.length;
  const angleStep = (2 * Math.PI) / n;
  const startAngle = -Math.PI / 2; // start from top

  const getPoint = (i: number, r: number) => ({
    x: cx + r * Math.cos(startAngle + i * angleStep),
    y: cy + r * Math.sin(startAngle + i * angleStep),
  });

  // Grid rings
  const gridRings = Array.from({ length: levels }, (_, lvl) => {
    const r = maxR * ((lvl + 1) / levels);
    const pts = data.map((_, i) => getPoint(i, r));
    return pts.map(p => `${p.x},${p.y}`).join(' ');
  });

  // Axis lines
  const axes = data.map((_, i) => getPoint(i, maxR));

  // Data polygon
  const dataPoints = data.map((d, i) => {
    const r = (d.A / d.fullMark) * maxR;
    return getPoint(i, r);
  });
  const dataPath = dataPoints.map(p => `${p.x},${p.y}`).join(' ');

  // Labels
  const labelPoints = data.map((d, i) => {
    const r = maxR + 48;
    return { ...getPoint(i, r), label: d.subject };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Grid */}
      {gridRings.map((pts, i) => (
        <polygon key={i} points={pts} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" />
      ))}
      {/* Axes */}
      {axes.map((p, i) => (
        <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" />
      ))}
      {/* Data shape */}
      <polygon points={dataPath} fill={color} fillOpacity="0.3" stroke={color} strokeWidth="4" />
      {/* Data dots */}
      {dataPoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="8" fill={color} />
      ))}
      {/* Labels */}
      {labelPoints.map((p, i) => (
        <text
          key={i}
          x={p.x}
          y={p.y}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="rgba(255,255,255,0.7)"
          fontSize={labelSize}
          fontWeight="900"
          fontFamily="system-ui, -apple-system, sans-serif"
          style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
        >
          {p.label}
        </text>
      ))}
    </svg>
  );
};

// ── Pure SVG Donut Chart ──

const SVGDonutChart: React.FC<{ data: { name: string; value: number }[]; size: number }> = ({ data, size }) => {
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size * 0.44;
  const innerR = size * 0.28;
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const gap = 0.02; // radians gap

  let currentAngle = -Math.PI / 2;
  const segments = data.map((d, i) => {
    const angle = (d.value / total) * (2 * Math.PI) - gap;
    const start = currentAngle + gap / 2;
    const end = start + angle;
    currentAngle += (d.value / total) * (2 * Math.PI);

    const x1o = cx + outerR * Math.cos(start);
    const y1o = cy + outerR * Math.sin(start);
    const x2o = cx + outerR * Math.cos(end);
    const y2o = cy + outerR * Math.sin(end);
    const x1i = cx + innerR * Math.cos(end);
    const y1i = cy + innerR * Math.sin(end);
    const x2i = cx + innerR * Math.cos(start);
    const y2i = cy + innerR * Math.sin(start);

    const largeArc = angle > Math.PI ? 1 : 0;

    const path = [
      `M ${x1o} ${y1o}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2o} ${y2o}`,
      `L ${x1i} ${y1i}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${x2i} ${y2i}`,
      'Z',
    ].join(' ');

    return { path, color: PIE_COLORS[i % PIE_COLORS.length] };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {segments.map((seg, i) => (
        <path key={i} d={seg.path} fill={seg.color} />
      ))}
    </svg>
  );
};

// ═══════════════════════════════════════════════════════════════
// SHARED CARD STYLES / LAYOUT
// ═══════════════════════════════════════════════════════════════

const CARD_W = 1080;
const CARD_H = 1920;

const cardBg: React.CSSProperties = {
  width: CARD_W,
  height: CARD_H,
  background: 'linear-gradient(180deg, #0a0a0a 0%, #0d1f1a 40%, #0a0e0c 100%)',
  position: 'relative',
  overflow: 'hidden',
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  color: 'white',
  display: 'flex',
  flexDirection: 'column',
};

// Particle dots (decorative) — larger for mobile visibility
const ParticleDots: React.FC = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
    {[
      { top: '6%', left: '10%', size: 8, opacity: 0.18 },
      { top: '14%', right: '16%', size: 6, opacity: 0.14 },
      { top: '28%', left: '82%', size: 10, opacity: 0.12 },
      { top: '38%', left: '4%', size: 7, opacity: 0.1 },
      { top: '50%', right: '8%', size: 8, opacity: 0.14 },
      { top: '62%', left: '22%', size: 6, opacity: 0.16 },
      { top: '72%', right: '25%', size: 10, opacity: 0.1 },
      { top: '82%', left: '55%', size: 8, opacity: 0.18 },
      { top: '92%', left: '12%', size: 6, opacity: 0.12 },
      { top: '88%', right: '12%', size: 8, opacity: 0.14 },
    ].map((dot, i) => (
      <div
        key={i}
        style={{
          position: 'absolute',
          top: dot.top,
          left: dot.left,
          right: (dot as any).right,
          width: dot.size,
          height: dot.size,
          borderRadius: '50%',
          background: '#99f6e4',
          opacity: dot.opacity,
        }}
      />
    ))}
    {/* Large soft glows */}
    <div style={{ position: 'absolute', top: '-8%', left: '50%', transform: 'translateX(-50%)', width: 900, height: 900, borderRadius: '50%', background: 'radial-gradient(circle, rgba(45,212,191,0.08) 0%, transparent 70%)' }} />
    <div style={{ position: 'absolute', bottom: '-8%', left: '50%', transform: 'translateX(-50%)', width: 1000, height: 1000, borderRadius: '50%', background: 'radial-gradient(circle, rgba(45,212,191,0.06) 0%, transparent 70%)' }} />
  </div>
);

const CardHeader: React.FC = () => (
  <div style={{ padding: '64px 72px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
    <img src="/images/bloom-landscape-white-logo.png" alt="Bloom" style={{ height: 56, opacity: 0.9 }} />
    <span style={{ fontSize: 28, color: 'rgba(255,255,255,0.35)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em' }}>Your Type</span>
  </div>
);

const CardFooter: React.FC<{ cta?: string }> = ({ cta = 'Discover your investor type' }) => (
  <div style={{ padding: '0 72px 64px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, position: 'relative', zIndex: 1, marginTop: 'auto' }}>
    <div style={{ width: '100%', height: 2, background: 'linear-gradient(90deg, transparent, rgba(153,246,228,0.3), transparent)' }} />
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, paddingTop: 20 }}>
      <span style={{ fontSize: 32, fontWeight: 900, color: '#99f6e4', textTransform: 'uppercase', letterSpacing: '0.15em' }}>{cta}</span>
      <span style={{ fontSize: 28, color: 'rgba(255,255,255,0.35)', fontWeight: 700, letterSpacing: '0.08em' }}>bloom.money/your-type</span>
    </div>
  </div>
);

// Trait pill — big and punchy for social
const TraitPill: React.FC<{ label: string; dark?: boolean }> = ({ label, dark }) => (
  <span style={{
    padding: '16px 36px',
    borderRadius: 999,
    fontSize: 28,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    ...(dark
      ? { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '2px solid rgba(255,255,255,0.1)' }
      : { background: '#99f6e4', color: '#000' }
    ),
  }}>
    {label}
  </span>
);


// ═══════════════════════════════════════════════════════════════
// CARD 1 — YOUR INVESTOR IDENTITY (General Overview)
// ═══════════════════════════════════════════════════════════════

const ShareCardIdentity = React.forwardRef<HTMLDivElement, { profile: InvestorProfile }>(({ profile }, ref) => {
  const bloomieImg = BLOOMIE_IMAGES[profile.id];
  return (
    <div ref={ref} style={cardBg}>
      <ParticleDots />
      <CardHeader />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 56, padding: '40px 72px', position: 'relative', zIndex: 1 }}>
        {/* Large Avatar — hero element */}
        {bloomieImg && (
          <div style={{
            width: 400, height: 400,
            borderRadius: 80,
            background: 'linear-gradient(135deg, rgba(153,246,228,0.12), rgba(255,255,255,0.04))',
            border: '3px solid rgba(255,255,255,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 120px rgba(45,212,191,0.12)',
          }}>
            <img src={bloomieImg.src || bloomieImg} alt={profile.name} style={{ width: 340, height: 340, objectFit: 'contain' }} />
          </div>
        )}

        {/* Identity — BIG and bold */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
          <span style={{ fontSize: 30, fontWeight: 900, color: '#99f6e4', textTransform: 'uppercase', letterSpacing: '0.35em' }}>
            I&apos;m a
          </span>
          <h1 style={{ fontSize: 88, fontWeight: 900, textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.0, margin: 0, background: 'linear-gradient(180deg, #fff 20%, rgba(255,255,255,0.6))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {profile.name}
          </h1>
        </div>

        {/* Traits — large pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
          {profile.traits.map(t => <TraitPill key={t} label={t} />)}
        </div>

        {/* Quick Stats — prominent */}
        <div style={{ display: 'flex', gap: 0, alignItems: 'stretch', background: 'rgba(255,255,255,0.04)', borderRadius: 32, border: '2px solid rgba(255,255,255,0.06)', overflow: 'hidden', width: '100%' }}>
          {[
            { label: 'Style', value: profile.investmentStyle },
            { label: 'Risk', value: profile.riskTolerance },
            { label: 'Horizon', value: profile.timeHorizon },
          ].map((stat, i) => (
            <div key={stat.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '28px 16px', borderLeft: i > 0 ? '2px solid rgba(255,255,255,0.06)' : 'none' }}>
              <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.35)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{stat.label}</span>
              <span style={{ fontSize: 30, fontWeight: 900, color: 'rgba(255,255,255,0.85)', textAlign: 'center' }}>{stat.value}</span>
            </div>
          ))}
        </div>
      </div>

      <CardFooter />
    </div>
  );
});
ShareCardIdentity.displayName = 'ShareCardIdentity';


// ═══════════════════════════════════════════════════════════════
// CARD 2 — PERFORMANCE DNA (Radar + Strengths)
// ═══════════════════════════════════════════════════════════════

const ShareCardDNA = React.forwardRef<HTMLDivElement, { profile: InvestorProfile }>(({ profile }, ref) => {
  const superpower = profile.detailedAnalysis[0];
  const titleParts = superpower?.title?.split(':') || [];
  const keyword = titleParts[1]?.trim() || superpower?.title || '';

  // Sorted radar scores
  const sortedScores = [...profile.radarData].sort((a, b) => b.A - a.A);

  return (
    <div ref={ref} style={cardBg}>
      <ParticleDots />
      <CardHeader />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 72px', position: 'relative', zIndex: 1, gap: 48 }}>
        {/* Title — massive */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 30, color: '#99f6e4', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.35em', marginBottom: 8 }}>My Performance</div>
          <div style={{ fontSize: 72, fontWeight: 900, textTransform: 'uppercase', lineHeight: 1.0, background: 'linear-gradient(180deg, #fff 20%, rgba(255,255,255,0.5))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>DNA</div>
        </div>

        {/* Large Radar — hero visual */}
        <SVGRadarChart data={profile.radarData} size={680} labelSize={38} />

        {/* Score Rankings — bold bars */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {sortedScores.map((d, i) => {
            const barWidth = (d.A / d.fullMark) * 100;
            return (
              <div key={d.subject} style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <span style={{ width: 200, fontSize: 28, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', color: i === 0 ? '#99f6e4' : 'rgba(255,255,255,0.55)', textAlign: 'right' }}>
                  {d.subject}
                </span>
                <div style={{ flex: 1, height: 28, background: 'rgba(255,255,255,0.06)', borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ width: `${barWidth}%`, height: '100%', borderRadius: 14, background: i === 0 ? 'linear-gradient(90deg, #99f6e4, #2dd4bf)' : `rgba(153,246,228,${0.45 - i * 0.08})` }} />
                </div>
                <span style={{ width: 60, fontSize: 32, fontWeight: 900, color: i === 0 ? '#99f6e4' : 'rgba(255,255,255,0.5)', textAlign: 'right' }}>{d.A}</span>
              </div>
            );
          })}
        </div>

        {/* Superpower callout — bold */}
        <div style={{
          width: '100%',
          background: 'rgba(153,246,228,0.08)', border: '2px solid rgba(153,246,228,0.2)',
          borderRadius: 36, padding: '40px 48px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center',
        }}>
          <span style={{ fontSize: 24, fontWeight: 900, color: 'rgba(153,246,228,0.6)', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Superpower</span>
          <span style={{ fontSize: 52, fontWeight: 900, color: '#99f6e4', textTransform: 'uppercase', lineHeight: 1.1 }}>{keyword}</span>
        </div>
      </div>

      <CardFooter cta="What's your superpower?" />
    </div>
  );
});
ShareCardDNA.displayName = 'ShareCardDNA';


// ═══════════════════════════════════════════════════════════════
// CARD 3 — YOUR MARKET TWIN (Fund Manager Match)
// ═══════════════════════════════════════════════════════════════

const ShareCardTwin = React.forwardRef<HTMLDivElement, { profile: InvestorProfile; manager: any | null }>(({ profile, manager }, ref) => {
  const bloomieImg = BLOOMIE_IMAGES[profile.id];

  return (
    <div ref={ref} style={cardBg}>
      <ParticleDots />
      <CardHeader />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 60, padding: '40px 72px', position: 'relative', zIndex: 1 }}>
        {/* Sparkle accents — larger */}
        <div style={{ position: 'absolute', top: '12%', left: '14%', fontSize: 60, color: 'rgba(255,255,255,0.08)' }}>✦</div>
        <div style={{ position: 'absolute', top: '10%', right: '16%', fontSize: 48, color: 'rgba(16,185,129,0.12)' }}>✧</div>
        <div style={{ position: 'absolute', bottom: '28%', left: '8%', fontSize: 40, color: 'rgba(255,255,255,0.06)' }}>✦</div>
        <div style={{ position: 'absolute', bottom: '22%', right: '10%', fontSize: 44, color: 'rgba(16,185,129,0.1)' }}>✧</div>

        {/* Overlapping Avatars — much bigger */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            width: 300, height: 300, borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(153,246,228,0.18), rgba(16,185,129,0.06))',
            border: '5px solid rgba(255,255,255,0.25)',
            boxShadow: '0 0 80px rgba(16,185,129,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            zIndex: 2, position: 'relative',
          }}>
            {bloomieImg && <img src={bloomieImg.src || bloomieImg} alt="" style={{ width: 260, height: 260, objectFit: 'contain' }} />}
          </div>
          <div style={{
            width: 300, height: 300, borderRadius: '50%',
            background: 'rgba(0,0,0,0.6)',
            border: '5px solid rgba(255,255,255,0.25)',
            boxShadow: '0 0 80px rgba(16,185,129,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            marginLeft: -60, zIndex: 1, position: 'relative',
          }}>
            {manager?.manager?.iconUrl ? (
              <img src={manager.manager.iconUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ fontSize: 100, opacity: 0.2 }}>👤</span>
            )}
          </div>
        </div>

        {/* "It's a Match!" — HUGE hero text */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
          <h2 style={{
            fontSize: 110, fontWeight: 900, lineHeight: 1, margin: 0,
            fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic',
            textShadow: '0 4px 60px rgba(16,185,129,0.4)',
          }}>
            It&apos;s a Match!
          </h2>
          {manager && (
            <p style={{ fontSize: 36, color: 'rgba(255,255,255,0.5)', fontWeight: 700, margin: 0, textAlign: 'center' }}>
              You matched with
            </p>
          )}
          {manager && (
            <p style={{ fontSize: 56, color: '#6ee7b7', fontWeight: 900, margin: 0, textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.1 }}>
              {manager.manager.name}
            </p>
          )}
        </div>

        {/* Tags — larger */}
        {manager && (
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
            {manager.manager.fundName && (
              <span style={{ padding: '16px 32px', borderRadius: 999, fontSize: 26, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '2px solid rgba(255,255,255,0.1)' }}>
                {manager.manager.fundName}
              </span>
            )}
            {manager.manager.investmentStyle && (
              <span style={{ padding: '16px 32px', borderRadius: 999, fontSize: 26, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', background: 'rgba(52,211,153,0.15)', color: '#6ee7b7', border: '2px solid rgba(52,211,153,0.25)' }}>
                {manager.manager.investmentStyle}
              </span>
            )}
          </div>
        )}
      </div>

      <CardFooter cta="Find your market twin" />
    </div>
  );
});
ShareCardTwin.displayName = 'ShareCardTwin';


// ═══════════════════════════════════════════════════════════════
// CARD 4 — YOUR CUSTOM PORTFOLIO (AI Blueprint)
// ═══════════════════════════════════════════════════════════════

const ShareCardPortfolio = React.forwardRef<HTMLDivElement, {
  profile: InvestorProfile;
  aiPortfolio: any;
  blueprintPieData: { name: string; value: number }[];
  blueprintAllStocks: { ticker: string; percentage: number }[];
}>(({ profile, aiPortfolio, blueprintPieData, blueprintAllStocks }, ref) => {
  const topStocks = blueprintAllStocks.slice(0, 5);

  return (
    <div ref={ref} style={cardBg}>
      <ParticleDots />
      <CardHeader />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 72px', position: 'relative', zIndex: 1, gap: 48 }}>
        {/* Title — big and clear */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 30, color: '#99f6e4', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.35em', marginBottom: 8 }}>My Custom</div>
          <div style={{ fontSize: 72, fontWeight: 900, textTransform: 'uppercase', lineHeight: 1.0, background: 'linear-gradient(180deg, #fff 20%, rgba(255,255,255,0.5))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Portfolio</div>
        </div>

        {/* Large Donut Chart — hero visual */}
        {blueprintPieData.length > 0 && (
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SVGDonutChart data={blueprintPieData} size={560} />
            <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 64, fontWeight: 900, color: 'rgba(255,255,255,0.9)' }}>{topStocks.length}+</span>
              <span style={{ fontSize: 24, fontWeight: 800, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Stocks</span>
            </div>
          </div>
        )}

        {/* Stock List — bold tickers */}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4, background: 'rgba(0,0,0,0.25)', borderRadius: 36, border: '2px solid rgba(255,255,255,0.06)', padding: '12px 0', overflow: 'hidden' }}>
          {topStocks.map((stock, i) => (
            <div key={stock.ticker} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 40px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <div style={{ width: 20, height: 20, borderRadius: 6, background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                <span style={{ fontSize: 36, fontWeight: 900, color: 'rgba(255,255,255,0.95)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stock.ticker}</span>
              </div>
              <span style={{ fontSize: 34, fontWeight: 800, color: PIE_COLORS[i % PIE_COLORS.length] }}>{stock.percentage.toFixed(1)}%</span>
            </div>
          ))}
          {blueprintAllStocks.length > 5 && (
            <div style={{ padding: '12px 40px', textAlign: 'center' }}>
              <span style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', fontWeight: 800 }}>+{blueprintAllStocks.length - 5} more assets</span>
            </div>
          )}
        </div>
      </div>

      <CardFooter cta="Get your custom portfolio" />
    </div>
  );
});
ShareCardPortfolio.displayName = 'ShareCardPortfolio';


// ═══════════════════════════════════════════════════════════════
// SHARE MODAL — Carousel + Save/Copy
// ═══════════════════════════════════════════════════════════════

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  profile: InvestorProfile;
  recommendations: any;
  aiPortfolio: any;
  blueprintPieData: { name: string; value: number }[];
  blueprintAllStocks: { ticker: string; percentage: number }[];
}

const CARD_LABELS = [
  'Your Identity',
  'Performance DNA',
  'Market Twin',
  'Custom Portfolio',
];

const ShareModal: React.FC<ShareModalProps> = ({
  open, onClose, profile, recommendations, aiPortfolio, blueprintPieData, blueprintAllStocks,
}) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const cardRefs = [
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
  ];

  const primaryManager = recommendations?.managers?.length > 0 ? recommendations.managers[0] : null;

  const generateBlob = useCallback(async (idx: number): Promise<Blob | null> => {
    const node = cardRefs[idx].current;
    if (!node) return null;

    try {
      const blob = await toBlob(node, {
        width: CARD_W,
        height: CARD_H,
        pixelRatio: 1,
        cacheBust: true,
        style: {
          transform: 'none',
          transformOrigin: 'top left',
        },
      });
      return blob;
    } catch (err) {
      console.error('[ShareModal] Failed to generate image:', err);
      return null;
    }
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const blob = await generateBlob(activeIdx);
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bloom-${profile.id.toLowerCase()}-${CARD_LABELS[activeIdx].replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setIsSaving(false);
    }
  }, [activeIdx, generateBlob, profile.id]);

  const handleCopy = useCallback(async () => {
    try {
      const blob = await generateBlob(activeIdx);
      if (!blob) return;

      if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback: trigger download instead
        handleSave();
      }
    } catch {
      // Fallback
      handleSave();
    }
  }, [activeIdx, generateBlob, handleSave]);

  const prev = () => setActiveIdx(i => Math.max(0, i - 1));
  const next = () => setActiveIdx(i => Math.min(CARD_LABELS.length - 1, i + 1));

  if (!open) return null;

  return (
    <>
      {/* Modal overlay */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-lg" />

        {/* Modal content */}
        <div
          className="relative bg-[#0d1117] w-full max-w-lg rounded-[2.5rem] overflow-hidden flex flex-col max-h-[90vh] shadow-2xl border border-white/10 animate-in fade-in zoom-in-95 duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 pb-2">
            <h3 className="text-lg font-bold uppercase tracking-widest text-white">Share Results</h3>
            <button onClick={onClose} className="size-10 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:bg-white/10 hover:text-white transition-all">
              <FontAwesomeIcon icon={faXmark as IconProp} className="size-4" />
            </button>
          </div>

          {/* Card Carousel Preview */}
          <div className="flex-1 overflow-hidden px-5 py-4">
            <div className="relative flex items-center justify-center">
              {/* Navigation Arrows */}
              <button
                onClick={prev}
                disabled={activeIdx === 0}
                className="absolute left-0 z-10 size-10 rounded-full bg-black/60 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-black/80 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faChevronLeft as IconProp} className="size-4" />
              </button>

              {/* Preview (scaled down from 1080x1920) */}
              <div className="w-[260px] h-[462px] rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black mx-auto" style={{ position: 'relative' }}>
                <div style={{
                  transform: `scale(${260 / CARD_W})`,
                  transformOrigin: 'top left',
                  width: CARD_W,
                  height: CARD_H,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}>
                  {activeIdx === 0 && <ShareCardIdentity profile={profile} />}
                  {activeIdx === 1 && <ShareCardDNA profile={profile} />}
                  {activeIdx === 2 && <ShareCardTwin profile={profile} manager={primaryManager} />}
                  {activeIdx === 3 && <ShareCardPortfolio profile={profile} aiPortfolio={aiPortfolio} blueprintPieData={blueprintPieData} blueprintAllStocks={blueprintAllStocks} />}
                </div>
              </div>

              <button
                onClick={next}
                disabled={activeIdx === CARD_LABELS.length - 1}
                className="absolute right-0 z-10 size-10 rounded-full bg-black/60 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-black/80 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faChevronRight as IconProp} className="size-4" />
              </button>
            </div>

            {/* Dot indicators + label */}
            <div className="flex flex-col items-center gap-2 mt-4">
              <div className="flex gap-2">
                {CARD_LABELS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${i === activeIdx ? 'w-8 bg-teal-200' : 'w-3 bg-white/20 hover:bg-white/30'}`}
                  />
                ))}
              </div>
              <span className="text-xs text-white/40 font-bold uppercase tracking-widest">{CARD_LABELS[activeIdx]}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="p-5 pt-2 flex gap-3">
            <Button
              onClick={handleSave}
              className="flex-1 h-12 rounded-2xl bg-teal-200 text-black hover:bg-teal-300 font-bold uppercase tracking-widest text-sm gap-2"
              disabled={isSaving}
            >
              <FontAwesomeIcon icon={faDownload as IconProp} className="size-4" />
              {isSaving ? 'Saving...' : 'Save Image'}
            </Button>
            <Button
              onClick={handleCopy}
              className="flex-1 h-12 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 font-bold uppercase tracking-widest text-sm gap-2"
            >
              <FontAwesomeIcon icon={(copied ? faCheck : faCopy) as IconProp} className="size-4" />
              {copied ? 'Copied!' : 'Copy Image'}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Hidden off-screen render targets for html-to-image ── */}
      <div style={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none', opacity: 0, zIndex: -1 }} aria-hidden="true">
        <ShareCardIdentity ref={cardRefs[0]} profile={profile} />
        <ShareCardDNA ref={cardRefs[1]} profile={profile} />
        <ShareCardTwin ref={cardRefs[2]} profile={profile} manager={primaryManager} />
        <ShareCardPortfolio ref={cardRefs[3]} profile={profile} aiPortfolio={aiPortfolio} blueprintPieData={blueprintPieData} blueprintAllStocks={blueprintAllStocks} />
      </div>
    </>
  );
};

export default ShareModal;

